import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { client } from '@frontend/features/common/services';
import { HotelLocationPicker } from '@frontend/features/hotels/HotelLocationPicker';
import type { HotelLocation } from '@frontend/features/hotels/searchModel';
import {
  effectivePolicyRoutes,
  DestinationPolicy,
  PolicyMode,
  PolicyRoute,
  PolicyRouteOption,
  SearchPolicy,
  policyPayload,
  policyValid,
  routeKey,
  toggleRoute,
} from './policy';

function RouteChoices({
  options,
  selected,
  disabled,
  change,
  additive = false,
  inherited = [],
}: {
  options: PolicyRouteOption[];
  selected: PolicyRoute[];
  disabled: boolean;
  change(value: PolicyRoute[]): void;
  additive?: boolean;
  inherited?: PolicyRoute[];
}) {
  const { t } = useTranslation();
  const missing = selected.filter((route) => !options.some((option) => routeKey(option) === routeKey(route)));
  return (
    <Stack>
      {!selected.length && (
        <Alert severity="warning">{t(additive ? 'hotelPolicy.emptyAdditions' : 'hotelPolicy.emptyAllowlist')}</Alert>
      )}
      {options.map((option) => (
        <FormControlLabel
          key={routeKey(option)}
          label={`${option.display_name} · ${option.access_code}${inherited.some((route) => routeKey(route) === routeKey(option)) ? ` — ${t('hotelPolicy.inherited')}` : ''}`}
          control={
            <Checkbox
              disabled={disabled || inherited.some((route) => routeKey(route) === routeKey(option))}
              checked={[...selected, ...inherited].some((route) => routeKey(route) === routeKey(option))}
              onChange={(_, checked) => change(toggleRoute(selected, option, checked))}
            />
          }
        />
      ))}
      {missing.map((route) => (
        <Alert key={routeKey(route)} severity="warning">
          {t('hotelPolicy.unavailableRoute')} {route.connector_account_id} / {route.access_code}{' '}
          <Button disabled={disabled} onClick={() => change(toggleRoute(selected, route, false))}>
            {t('hotelPolicy.remove')}
          </Button>
        </Alert>
      ))}
      {!options.length && <Typography>{t('hotelPolicy.noRoutes')}</Typography>}
    </Stack>
  );
}
export function HotelSearchPolicy({ agencyId }: { agencyId: number }) {
  const { t } = useTranslation();
  const cache = useQueryClient();
  const path = `agencies/${agencyId}/hotels/search-policy`;
  const key = ['hotel-search-policy', agencyId];
  const policy = useQuery(key, async () => (await client.get<{ data: SearchPolicy }>(path)).data.data, {
    enabled: Number.isSafeInteger(agencyId) && agencyId > 0,
    retry: false,
  });
  const options = useQuery(
    [...key, 'routes'],
    async () =>
      (
        await client.get<{
          data: PolicyRouteOption[];
          unsupported_accounts: Array<{ connector_account_id: string; display_name: string; access_mode: string }>;
        }>(`${path}/routes`)
      ).data,
    { enabled: !!policy.data, retry: false },
  );
  const [draft, setDraft] = useState<SearchPolicy | null>(null);
  const [reason, setReason] = useState('');
  const [location, setLocation] = useState<HotelLocation | null>(null);
  const [resolving, setResolving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const save = useMutation(
    async () => {
      if (!draft || conflict || !policyValid(draft, reason)) throw new Error('Invalid policy');
      await client.put(path, policyPayload(draft, reason));
    },
    {
      onSuccess: () => {
        setDraft(null);
        cache.invalidateQueries(key);
      },
      onError: (error: any) => {
        if (error?.response?.status === 409) setConflict(true);
      },
    },
  );
  const modeOptions = (inherit = false) =>
    [...(inherit ? ['inherit', 'extend'] : []), 'all_enabled', 'allowlist', 'disabled'].map((mode) => (
      <MenuItem key={mode} value={mode}>
        {t(`hotelPolicy.${mode}`)}
      </MenuItem>
    ));
  const summary = (value: SearchPolicy, destination?: DestinationPolicy) => {
    if (options.isLoading) return <Typography>{t('hotelPolicy.loading')}</Typography>;
    if (options.isError) return <Alert severity="error">{t('hotelPolicy.routesError')}</Alert>;
    const usesAll =
      destination?.mode === 'all_enabled' ||
      ((!destination || destination.mode === 'inherit' || destination.mode === 'extend') &&
        value.default_mode === 'all_enabled');
    if (usesAll && options.data?.unsupported_accounts?.length) return <Alert severity="warning">{t('hotelPolicy.unsupportedAccounts')}</Alert>;
    const available = options.data?.data ?? [];
    const inherited = new Set(effectivePolicyRoutes(value, available).map(routeKey));
    const effective = effectivePolicyRoutes(value, available, destination);
    return (
      <Box sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 1 }}>
        <Typography variant="subtitle2">{t('hotelPolicy.effective')}</Typography>
        {!effective.length && <Typography>{t('hotelPolicy.noneEffective')}</Typography>}
        {effective.map((route) => (
          <Typography key={routeKey(route)} variant="body2">
            {route.display_name} · {route.access_code}
            {destination?.mode === 'extend' &&
              ` — ${t(inherited.has(routeKey(route)) ? 'hotelPolicy.inherited' : 'hotelPolicy.additional')}`}
          </Typography>
        ))}
      </Box>
    );
  };
  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5">{t('hotelPolicy.title')}</Typography>
      <Alert severity="info" sx={{ my: 2 }}>
        {t('hotelPolicy.scope')}
      </Alert>
      {policy.isLoading && <Typography>{t('hotelPolicy.loading')}</Typography>}
      {policy.isError && (
        <Alert severity="error">
          {t('hotelPolicy.loadError')} <Button onClick={() => policy.refetch()}>{t('hotelPolicy.reload')}</Button>
        </Alert>
      )}
      {!!options.data?.unsupported_accounts?.length && (
        <Alert severity="warning">
          {t('hotelPolicy.unsupportedAccounts')}{' '}
          {options.data.unsupported_accounts.map((account) => account.display_name).join(', ')}
        </Alert>
      )}
      {policy.data && (
        <Stack spacing={1}>
          <Typography>
            {t('hotelPolicy.default')}: {t(`hotelPolicy.${policy.data.default_mode}`)}
          </Typography>
          {summary(policy.data)}
          <Typography>{t('hotelPolicy.overrides', { count: policy.data.destination_overrides.length })}</Typography>
          <Button
            onClick={() => {
              setDraft(JSON.parse(JSON.stringify(policy.data)));
              setReason('');
              setLocation(null);
              setConflict(false);
              save.reset();
            }}
          >
            {t('hotelPolicy.edit')}
          </Button>
        </Stack>
      )}
      <Dialog
        open={!!draft}
        fullWidth
        maxWidth="md"
        onClose={() => {
          if (!save.isLoading) setDraft(null);
        }}
      >
        <DialogTitle>{t('hotelPolicy.title')}</DialogTitle>
        <DialogContent>
          {draft && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert severity="info">{t('hotelPolicy.pointDefault')}</Alert>
              <TextField
                select
                label={t('hotelPolicy.default')}
                value={draft.default_mode}
                disabled={conflict || save.isLoading}
                onChange={(e) => setDraft({ ...draft, default_mode: e.target.value as PolicyMode })}
              >
                {modeOptions()}
              </TextField>
              {draft.default_mode === 'all_enabled' && (
                <Alert severity="warning">{t('hotelPolicy.includesFuture')}</Alert>
              )}
              {options.isError && <Alert severity="error">{t('hotelPolicy.routesError')}</Alert>}
              {draft.default_mode === 'allowlist' && (
                <RouteChoices
                  options={options.data?.data ?? []}
                  selected={draft.default_routes}
                  disabled={conflict || save.isLoading}
                  change={(routes) => setDraft({ ...draft, default_routes: routes })}
                />
              )}
              {summary(draft)}
              {draft.destination_overrides.map((row, index) => (
                <Box key={row.place_id} sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}>
                  <Typography>{row.display_name ?? row.place_id}</Typography>
                  {row.display_name && <Typography variant="caption">{row.place_id}</Typography>}
                  <TextField
                    select
                    fullWidth
                    sx={{ mt: 2 }}
                    SelectProps={{ sx: { '& .MuiSelect-select': { whiteSpace: 'normal' } } }}
                    label={t('hotelPolicy.destinationRule')}
                    value={row.mode}
                    disabled={conflict || save.isLoading}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        destination_overrides: draft.destination_overrides.map((item, i) =>
                          i === index ? { ...item, mode: e.target.value as typeof row.mode } : item,),
                      })
                    }
                  >
                    {modeOptions(true)}
                  </TextField>
                  {(row.mode === 'allowlist' || row.mode === 'extend') && (
                    <RouteChoices
                      options={options.data?.data ?? []}
                      inherited={row.mode === 'extend' ? effectivePolicyRoutes(draft, options.data?.data ?? []) : []}
                      additive={row.mode === 'extend'}
                      selected={row.routes}
                      disabled={conflict || save.isLoading}
                      change={(routes) =>
                        setDraft({
                          ...draft,
                          destination_overrides: draft.destination_overrides.map((item, i) =>
                            i === index ? { ...item, routes } : item,),
                        })
                      }
                    />
                  )}
                  {summary(draft, row)}
                  <Button
                    disabled={conflict || save.isLoading}
                    onClick={() =>
                      setDraft({
                        ...draft,
                        destination_overrides: draft.destination_overrides.filter((_, i) => i !== index),
                      })
                    }
                  >
                    {t('hotelPolicy.remove')}
                  </Button>
                </Box>
              ))}
              {!conflict && !save.isLoading && (
                <>
                  <Typography>{t('hotelPolicy.addDestination')}</Typography>
                  <HotelLocationPicker value={location} onChange={setLocation} onResolving={setResolving} />
                  {location?.type === 'point' && <Alert severity="info">{t('hotelPolicy.pointDefault')}</Alert>}
                  <Button
                    disabled={
                      resolving ||
                      location?.type !== 'destination' ||
                      draft.destination_overrides.length >= 200 ||
                      draft.destination_overrides.some(
                        (row) => location?.type === 'destination' && row.place_id === location.placeId,
                      )
                    }
                    onClick={() => {
                      if (location?.type === 'destination') {
                        setDraft({
                          ...draft,
                          destination_overrides: [
                            ...draft.destination_overrides,
                            {
                              source: 'ubikeishon',
                              place_id: location.placeId,
                              ...(location.label.trim() ? { display_name: location.label.trim().slice(0, 200) } : {}),
                              mode: 'inherit',
                              routes: [],
                            },
                          ],
                        });
                        setLocation(null);
                      }
                    }}
                  >
                    {t('hotelPolicy.add')}
                  </Button>
                </>
              )}
              <TextField
                label={t('hotelPolicy.reason')}
                value={reason}
                disabled={conflict || save.isLoading}
                inputProps={{ maxLength: 1000 }}
                onChange={(e) => setReason(e.target.value)}
              />
              {save.isError && (
                <Alert severity="error">{t(conflict ? 'hotelPolicy.conflict' : 'hotelPolicy.saveError')}</Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={save.isLoading} onClick={() => setDraft(null)}>
            {t('hotelPolicy.close')}
          </Button>
          {conflict ? (
            <Button
              onClick={() => {
                setDraft(null);
                policy.refetch();
                options.refetch();
              }}
            >
              {t('hotelPolicy.reload')}
            </Button>
          ) : (
            <Button
              disabled={!draft || !policyValid(draft, reason) || save.isLoading || options.isLoading || options.isError}
              onClick={() => save.mutate()}
            >
              {t('hotelPolicy.save')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
